/**
 * <email-consent-form>
 * Email marketing opt-in with an explicit consent checkbox.
 * Wraps Shopify's native customer form so bot protection still applies;
 * this only gates the submit button, scopes the success message, and
 * remembers that the shopper has already subscribed.
 */
const STORE_KEY = 'ecf_last_submitted';
const SUBSCRIBED_KEY = 'ecf_subscribed';

/* Read once, at module load, so every instance on the page sees the same
   value — otherwise the first instance would clear it for the others. */
let LAST_SUBMITTED = null;
try {
  LAST_SUBMITTED = sessionStorage.getItem(STORE_KEY);
  sessionStorage.removeItem(STORE_KEY);
} catch (e) {}

let ALREADY_SUBSCRIBED = false;
try {
  ALREADY_SUBSCRIBED = localStorage.getItem(SUBSCRIBED_KEY) === '1';
} catch (e) {}

function rememberSubscribed() {
  ALREADY_SUBSCRIBED = true;
  try {
    localStorage.setItem(SUBSCRIBED_KEY, '1');
  } catch (e) {}
  document.dispatchEvent(new CustomEvent('ecf:subscribed'));
}

class EmailConsentForm extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ecfReady === 'true') return;
    this.dataset.ecfReady = 'true';

    this.form = this.querySelector('form');
    this.input = this.querySelector('[data-ecf-email]');
    this.consent = this.querySelector('[data-ecf-consent]');
    this.button = this.querySelector('[data-ecf-submit]');
    this.message = this.querySelector('[data-ecf-message]');
    this.fields = this.querySelector('[data-ecf-fields]');
    this.subscribedNote = this.querySelector('[data-ecf-subscribed]');

    document.addEventListener('ecf:subscribed', () => {
      if (!this.classList.contains('ecf--done')) this.showSubscribed();
    });

    /* Logged-in customer who already accepts marketing — rendered server side. */
    if (this.dataset.subscribed === 'true') {
      this.showSubscribed();
      return;
    }

    if (!this.form || !this.input || !this.consent || !this.button) return;

    this.consent.addEventListener('change', () => this.sync());
    this.input.addEventListener('input', () => this.sync());
    this.form.addEventListener('submit', (event) => this.onSubmit(event));

    this.sync();

    /* Just subscribed on this page load → confirmation. */
    if (this.restore()) return;

    /* Subscribed earlier → don't show the form again. */
    if (ALREADY_SUBSCRIBED) this.showSubscribed();
  }

  get valid() {
    const value = (this.input.value || '').trim();
    return this.consent.checked && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value);
  }

  sync() {
    this.button.disabled = !this.valid;
    if (this.consent.checked) this.say('', '');
  }

  say(text, kind) {
    if (!this.message) return;
    this.message.textContent = text;
    this.message.className = 'ecf__message' + (kind ? ' ecf__message--' + kind : '');
    this.message.hidden = !text;
  }

  hideFields() {
    if (this.fields) this.fields.hidden = true;
    this.classList.add('ecf--done');
  }

  /* After a successful post Shopify re-renders every customer form as
     "posted successfully". Only confirm on the one actually submitted. */
  restore() {
    if (!this.querySelector('[data-ecf-posted]')) return false;
    if (LAST_SUBMITTED && LAST_SUBMITTED !== this.dataset.key) {
      rememberSubscribed();
      this.showSubscribed();
      return true;
    }
    rememberSubscribed();
    this.say(this.dataset.success || 'You are subscribed. Check your inbox.', 'success');
    this.hideFields();
    return true;
  }

  showSubscribed() {
    this.hideFields();
    this.say('', '');
    if (this.subscribedNote) this.subscribedNote.hidden = false;
    this.classList.add('ecf--subscribed');
  }

  onSubmit(event) {
    if (!this.consent.checked) {
      event.preventDefault();
      this.say(this.dataset.consentError || 'Please confirm you want to receive email marketing.', 'error');
      return;
    }
    if (!this.valid) {
      event.preventDefault();
      this.say(this.dataset.emailError || 'Please enter a valid email address.', 'error');
      return;
    }
    try {
      sessionStorage.setItem(STORE_KEY, this.dataset.key || '');
    } catch (e) {}
  }
}

if (!customElements.get('email-consent-form')) {
  customElements.define('email-consent-form', EmailConsentForm);
}
